import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Zap } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { RateioUnidadeGeradora } from '../../../types';

interface RegistrarGeracaoModalProps {
  ug: RateioUnidadeGeradora;
  meses: string[];
  mesInicial: string;
  onClose: () => void;
  onSalvo: () => void;
}

const RegistrarGeracaoModal: React.FC<RegistrarGeracaoModalProps> = ({ ug, meses, mesInicial, onClose, onSalvo }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();

  const [mes, setMes] = useState(mesInicial);
  const [geracaoKwh, setGeracaoKwh] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const carregarExistente = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('rateio_geracao_mensal')
          .select('geracao_kwh')
          .eq('rateio_unidade_geradora_id', ug.id)
          .eq('mes_referencia', mes)
          .maybeSingle();
        if (error) throw error;
        setGeracaoKwh(data ? String(data.geracao_kwh) : '');
      } catch (error) {
        console.error('Error loading geração mensal', error);
      } finally {
        setLoading(false);
      }
    };
    carregarExistente();
  }, [ug.id, mes]);

  const handleSalvar = async () => {
    const valor = parseFloat(geracaoKwh.replace(',', '.'));
    if (!Number.isFinite(valor) || valor < 0) {
      addToast('Informe um valor de geração válido (kWh).', 'error');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.from('rateio_geracao_mensal').upsert(
        {
          rateio_unidade_geradora_id: ug.id,
          mes_referencia: mes,
          geracao_kwh: valor,
          registrado_por: profile?.id,
          registrado_por_nome: profile?.full_name || null,
        },
        { onConflict: 'rateio_unidade_geradora_id,mes_referencia' }
      );
      if (error) throw error;
      addToast('Geração mensal registrada.', 'success');
      logUsageEvent({ userId: profile?.id, modulo: 'gestao', funcionalidade: 'rateio_dashboard', acao: 'registrar_geracao_mensal', origem: 'acao' });
      onSalvo();
    } catch (error) {
      console.error('Error saving geração mensal', error);
      addToast('Erro ao registrar geração mensal.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white font-poppins flex items-center gap-2">
            <Zap size={18} className="text-primary" /> Registrar Geração — {ug.nome}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mês de referência</label>
            <select
              value={mes}
              onChange={e => setMes(e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
            >
              {meses.map(m => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Geração (kWh)</label>
            <input
              type="number"
              step="0.01"
              min={0}
              value={geracaoKwh}
              onChange={e => setGeracaoKwh(e.target.value)}
              placeholder="0,00"
              disabled={loading}
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 disabled:opacity-60"
            />
            {loading && <p className="text-xs text-gray-400 mt-1">Carregando registro existente…</p>}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleSalvar}
            disabled={saving || loading}
            className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RegistrarGeracaoModal;
